// Row-rendering helpers shared by the Proxy/CA/Issuer table (IntakeQueuePage)
// and the Regulatory tab (RegulatoryIntakeQueue) — kept in one place so both
// tabs render the "same columns" identically and don't fall out of sync.
import React, { useState } from 'react';
import { noteStamp, latestReason } from '../data/receiptDraft';
import { updateMaterialReceipt } from '../services/materialReceiptService';
import type { MaterialReceiptRow } from '../types/api';
import { StatusPill } from './StatusPill';

const INP = 'w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white';

// Exceptions that always need a human — surfaced in the Needs Attention rail.
export const ATTENTION_STATUSES = ['No Job Found', 'Received Short', 'On Hold'];

// Severity accent for the Needs Attention cards — danger red for hard errors,
// the rail-header amber (#b45309) for On Hold; fallback is the muted token.
const SEVERITY: Record<string, string> = {
  'No Job Found':   '#e7000b',
  'Received Short': '#e7000b',
  'On Hold':        '#b45309',
};

const CATEGORY: Record<string, { label: string; cls: string }> = {
  P:  { label: 'Proxy',       cls: 'tp tp-px' },
  CA: { label: 'Corp Action', cls: 'tp tp-ca' },
  R:  { label: 'Regulatory',  cls: 'tp tp-rg' },
  I:  { label: 'Issuer',      cls: 'tp tp-px' },
};

export function CategoryTag({ category }: { category: string | null }) {
  const c = category ? CATEGORY[category] : null;
  return c ? <span className={c.cls}>{c.label}</span> : <span className="text-xs text-muted">—</span>;
}

const isoDaysAgo = (n: number) => new Date(Date.now() - n * 86400000).toISOString().split('T')[0];

export function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const today = isoDaysAgo(0);
  const yest = isoDaysAgo(1);
  if (iso === today) return 'Today';
  if (iso === yest) return 'Yesterday';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Compact user initials, e.g. "joanne" → "JO", "mary chen" → "MC".
export function initials(name: string | null): string {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/);
  return (parts.length > 1 ? parts.map(p => p[0]).join('') : name.slice(0, 2)).toUpperCase();
}

export function EditModal({ row, onClose, onSaved, showToast }: { row: MaterialReceiptRow; onClose: () => void; onSaved: (r: MaterialReceiptRow) => void; showToast?: (m: string) => void }) {
  const [scan, setScan] = useState(row.scan_number);
  const [clients, setClients] = useState(row.client_names.join(', '));
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      const updated = await updateMaterialReceipt(row.id, { scan_number: scan, client_names: clients.split(',').map(s => s.trim()).filter(Boolean) });
      onSaved(updated); showToast?.('Receipt updated'); onClose();
    } catch { showToast?.('Failed to save'); } finally { setSaving(false); }
  }
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(4,50,67,.5)' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-modal w-[440px] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-trim">
          <span className="text-base font-semibold text-ink">Edit Receipt</span>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-copy hover:bg-wash"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg></button>
        </div>
        <div className="px-5 py-4 flex flex-col gap-3">
          <div><label className="block text-xs font-medium text-copy uppercase tracking-wide mb-1">Scan Number</label><input className={INP} value={scan} onChange={e => setScan(e.target.value)} /></div>
          <div><label className="block text-xs font-medium text-copy uppercase tracking-wide mb-1">Client Name(s) — comma separated</label><input className={INP} value={clients} onChange={e => setClients(e.target.value)} /></div>
        </div>
        <div className="px-5 pb-5 pt-3 border-t border-trim flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-trim text-sm font-medium text-copy hover:bg-wash">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// Appends a typed 'reason' entry to the note log — the latest one is the
// exception headline everywhere (replaces the old mutable exception_reason).
export function CommentModal({ row, author, onClose, onSaved, showToast }: { row: MaterialReceiptRow; author: string; onClose: () => void; onSaved: (r: MaterialReceiptRow) => void; showToast?: (m: string) => void }) {
  const current = latestReason(row.note_log);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!note.trim()) return;
    setSaving(true);
    try {
      const updated = await updateMaterialReceipt(row.id, {
        note_log: [...(row.note_log || []), { author, timestamp: noteStamp(), text: note.trim(), kind: 'reason' }],
      });
      onSaved(updated); showToast?.('Reason updated'); onClose();
    } catch { showToast?.('Failed to save note'); } finally { setSaving(false); }
  }
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(4,50,67,.5)' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-modal w-[460px] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-trim">
          <span className="text-base font-semibold text-ink">Exception Reason — {row.scan_number}</span>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-copy hover:bg-wash"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg></button>
        </div>
        <div className="px-5 py-4 flex flex-col gap-3">
          {current && (
            <div className="text-xs text-copy bg-wash rounded-lg px-3 py-2">
              <span className="font-semibold">{current.author}</span> <span className="text-muted">· {current.timestamp}</span>
              <div className="mt-0.5">{current.text}</div>
            </div>
          )}
          <textarea autoFocus className={`${INP} resize-none`} rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder={current ? 'Update the reason…' : 'Why is this an exception?'} />
        </div>
        <div className="px-5 pb-5 pt-3 border-t border-trim flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-trim text-sm font-medium text-copy hover:bg-wash">Cancel</button>
          <button onClick={save} disabled={saving || !note.trim()} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 disabled:opacity-60">{saving ? 'Saving…' : 'Save Reason'}</button>
        </div>
      </div>
    </div>
  );
}

// Compact exception card for the Needs Attention rail.
export function ExceptionCard({ row, onOpen }: { row: MaterialReceiptRow; onOpen: (r: MaterialReceiptRow) => void }) {
  return (
    <button
      onClick={() => onOpen(row)}
      className="w-full text-left rounded-lg border border-trim bg-white px-3 py-2.5 hover:shadow-card transition-all"
      style={{ borderLeftWidth: 3, borderLeftColor: SEVERITY[row.material_status] ?? '#94a3b8' }}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <StatusPill status={row.material_status} />
        <span className="text-xs text-muted whitespace-nowrap">{fmtDate(row.intake_date)}</span>
      </div>
      <div className="text-xs font-medium text-ink truncate">{row.scan_number}</div>
      <div className="text-xs text-copy truncate" title={row.client_names.join(', ')}>{row.client_names.join(', ') || '—'}</div>
      {latestReason(row.note_log) && (
        <div className="mt-1 text-xs text-muted line-clamp-2">{latestReason(row.note_log)!.text}</div>
      )}
    </button>
  );
}

// Toolbar icon that opens the Needs Attention rail — badge shows the open count.
export function AttentionToggleButton({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title="Needs Attention"
      className="relative w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
      style={{ background: count > 0 ? '#FEF3C7' : '#F5F7FA' }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
      {count > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center tabular-nums" style={{ background: '#e7000b', color: 'white' }}>{count}</span>
      )}
    </button>
  );
}

// Collapsible rail of every open exception (ATTENTION_STATUSES) — neutral
// surface; severity is carried by each card's coloured left border.
export function NeedsAttentionRail({ rows, onOpen, onClose }: { rows: MaterialReceiptRow[]; onOpen: (r: MaterialReceiptRow) => void; onClose: () => void }) {
  return (
    <aside className="flex-shrink-0 border-l border-trim bg-wash flex flex-col" style={{ width: 300 }}>
      <div className="flex items-center gap-2 px-4 pt-4 pb-4 border-b border-trim flex-shrink-0">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-ink">Needs Attention</h2>
        {rows.length > 0 && (
          <span className="px-1.5 py-0.5 rounded-full text-xs font-bold tabular-nums" style={{ background: '#FEE2E2', color: '#b91c1c' }}>{rows.length}</span>
        )}
        <button onClick={onClose} title="Collapse" className="ml-auto w-6 h-6 rounded flex items-center justify-center text-copy hover:bg-white">
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-2">
        {rows.length === 0 ? (
          <div className="text-xs text-muted text-center py-8 px-2">No open exceptions — all scans are on track.</div>
        ) : (
          rows.map(r => <ExceptionCard key={r.id} row={r} onOpen={onOpen} />)
        )}
      </div>
    </aside>
  );
}
